import React, { useState } from 'react';
import styles from './cta-hero.module.scss';
import IButton from '../../../models/button/IButton';
import Button from '../../atoms/button/button';
import IImage from '../../../models/viewModels/base/IImage';
import colorStyle from '../../../styles/utils/variables.module.scss';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ApplicationSettings } from '../../../models/settings';
import ImageComponent from '../../atoms/image-component/image-component';
import ICtaHero from '../../../models/cta-hero/ICtaHero';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';

export type CtaHeroProps = {
  ctaHero: ICtaHero;
  appSettings: ApplicationSettings;
};

// Sub-component for the image section
const CtaHeroImage = ({ image }: { image: IImage }) => {
  const src =
    image?.secureUrl ||
    'https://media.intostudy.com/image/upload/v1681806343/Marketing_Team/Global/Images/PP-news-mid_page-placeholder.png';
  const altText = image?.alt || 'Image unavailable';
  const width = image?.width ? +image.width : 1200;
  const height = image?.height ? +image.height : 500;

  return <ImageComponent src={src} altText={altText} width={width} height={height} />;
};

// Sub-component for the text and button section
const CtaHeroText = ({
  title,
  copyText,
  buttonProps,
  appSettings,
}: {
  title?: string;
  copyText?: string;
  buttonProps?: IButton;
  appSettings: ApplicationSettings;
}) => {
  const getButtonColors = (buttonStyle: ButtonStyleType) => ({
    copyColor:
      buttonStyle === ButtonStyleType.Outline
        ? appSettings.branding.primaryColor
        : colorStyle.white,
    backgroundColor:
      buttonStyle === ButtonStyleType.Outline
        ? colorStyle.white
        : appSettings.branding.primaryColor,
  });

  const { copyColor, backgroundColor } = getButtonColors(
    buttonProps?.buttonStyle || ButtonStyleType.Filled,
  );

  return (
    <>
      {title && <h1 className={`${styles['cta-hero_heading']} m-0 mb-2 mb-md-4`}>{title}</h1>}
      {copyText && <p className={`${styles['cta-hero_desc']} m-0 mb-4`}>{copyText}</p>}
      {buttonProps && (
        <Button
          buttonID={buttonProps.buttonID}
          buttonURL={buttonProps.buttonURL || '#'}
          buttonNewTab={buttonProps.buttonNewTab}
          buttonAriaLabel={buttonProps.buttonAriaLabel}
          buttonRole={buttonProps.buttonRole}
          buttonName={buttonProps.buttonName || 'Link unavailable'}
          buttonSize={buttonProps.buttonSize}
          buttonStyle={buttonProps.buttonStyle || ButtonStyleType.Filled}
          copyColor={copyColor}
          backgroundColor={backgroundColor}
          outlineColor={appSettings.branding.primaryColor}
          hoverBackgroundColor={appSettings.branding.primaryHoverColor}
          activeBackgroundColor={appSettings.branding.primaryActiveColor}
          heapIdentifier={buttonProps.buttonID}
          buttonType={buttonProps.buttonType}
        >
          {buttonProps.children || 'Link unavailable'}
        </Button>
      )}
    </>
  );
};

// Main CtaHero component
export default function CtaHero({ ctaHero, appSettings }: CtaHeroProps) {
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Medium), 100);

  const layoutClass = ctaHero?.layout === 'Full Width' ? styles['full-width'] : styles['default'];

  const shouldRenderContent = ctaHero?.title || ctaHero?.copyText || ctaHero?.button;

  return (
    <div className={`${styles['cta-hero']} ${layoutClass}`}>
      <div className={styles['cta-hero_wrapper']}>
        {ctaHero?.id && <div className='anchor' id={ctaHero?.id}></div>}
        {shouldRenderContent && (
          <div className={styles['cta-hero_content']}>
            <CtaHeroText
              title={ctaHero?.title}
              copyText={ctaHero?.copyText}
              buttonProps={ctaHero?.button}
              appSettings={appSettings}
            />
          </div>
        )}
        <CtaHeroImage image={isBreakpointUp ? ctaHero?.imageDesktop : ctaHero?.imageMobile} />
      </div>
    </div>
  );
}
